import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2, Rocket, Upload } from 'lucide-react'

import { publishCreativeToWorks } from '@/api/studio-creative'
import {
  clipOptionsToPublishPayload,
  defaultSelectedCreativePublishKeys,
  listCreativePublishClipOptions,
  type CreativePublishClipOption,
} from '@/features/studio/canvas/creative-publish-clips'
import { resolveApiErrorMessage } from '@/features/studio/lib/resolveApiError'
import { ProtoBtn, ProtoModal } from '@/features/studio/components/ui-kit'
import type { StudioFlowNode } from '@/features/studio/canvas/types'
import { Checkbox } from '@/shared/components/ui/checkbox'
import { resolveMediaDisplayUrl } from '@/shared/lib/media-proxy'

export function CreativePublishWorksModal({
  open,
  onClose,
  workspaceId,
  nodes,
  focusNodeId,
  onBeforeSubmit,
  onSuccess,
}: {
  open: boolean
  onClose: () => void
  workspaceId: string
  nodes: StudioFlowNode[]
  focusNodeId?: string | null
  onBeforeSubmit?: () => Promise<boolean>
  onSuccess: (message: string) => void
}) {
  const { t } = useTranslation()
  const clips = useMemo(() => listCreativePublishClipOptions(nodes), [nodes])
  const [selected, setSelected] = useState<Set<string>>(() => new Set())
  const [submitting, setSubmitting] = useState<'sync' | 'publish' | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    setSelected(defaultSelectedCreativePublishKeys(clips, focusNodeId))
    setError(null)
  }, [open, clips, focusNodeId])

  function toggleClip(key: string) {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  async function handleSubmit(publish: boolean) {
    if (selected.size === 0) {
      setError(t('creative.publish.selectAtLeastOne'))
      return
    }
    setSubmitting(publish ? 'publish' : 'sync')
    setError(null)
    try {
      if (onBeforeSubmit) {
        const ok = await onBeforeSubmit()
        if (!ok) {
          setSubmitting(null)
          return
        }
      }
      const payload = clipOptionsToPublishPayload(clips, selected)
      const res = await publishCreativeToWorks(workspaceId, { publish, clips: payload })
      onSuccess(
        t('timeline.publishSuccess', {
          id: res.work_public_id,
          n: res.episodes_synced,
          status: res.work_status,
        }),
      )
      onClose()
    } catch (e) {
      setError(resolveApiErrorMessage(e, 'timeline.publishFailed'))
    } finally {
      setSubmitting(null)
    }
  }

  return (
    <ProtoModal
      open={open}
      onClose={onClose}
      title={t('creative.publish.title')}
      footer={
        <>
          <ProtoBtn size="sm" variant="secondary" onClick={onClose} disabled={submitting != null}>
            {t('creative.publish.cancel')}
          </ProtoBtn>
          <ProtoBtn
            size="sm"
            variant="secondary"
            disabled={submitting != null || clips.length === 0}
            onClick={() => void handleSubmit(false)}
          >
            {submitting === 'sync' ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Upload className="h-4 w-4" />
            )}
            {t('timeline.syncWorks')}
          </ProtoBtn>
          <ProtoBtn
            size="sm"
            disabled={submitting != null || clips.length === 0}
            onClick={() => void handleSubmit(true)}
          >
            {submitting === 'publish' ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Rocket className="h-4 w-4" />
            )}
            {t('timeline.publishWorks')}
          </ProtoBtn>
        </>
      }
    >
      {error ? <p className="mb-3 text-sm text-rose-300">{error}</p> : null}
      <p className="mb-4 text-xs text-zinc-500">{t('creative.publish.hint')}</p>
      {clips.length === 0 ? (
        <p className="text-sm text-zinc-400">{t('creative.publish.empty')}</p>
      ) : (
        <ul className="max-h-[min(420px,50vh)] space-y-2 overflow-y-auto pr-1">
          {clips.map((clip) => (
            <ClipRow key={clip.key} clip={clip} checked={selected.has(clip.key)} onToggle={() => toggleClip(clip.key)} />
          ))}
        </ul>
      )}
      <p className="mt-3 text-xs text-zinc-500">
        {t('creative.publish.selectedCount', { n: selected.size, total: clips.length })}
      </p>
    </ProtoModal>
  )
}

function ClipRow({
  clip,
  checked,
  onToggle,
}: {
  clip: CreativePublishClipOption
  checked: boolean
  onToggle: () => void
}) {
  const { t } = useTranslation()
  const preview = resolveMediaDisplayUrl(clip.previewUrl)

  return (
    <li>
      <label className="flex cursor-pointer items-center gap-3 rounded-lg border border-[#2a2a3a] bg-[#1b1b27] px-3 py-2 hover:border-indigo-500/40">
        <Checkbox checked={checked} onCheckedChange={onToggle} />
        <div className="h-12 w-20 shrink-0 overflow-hidden rounded-md bg-black/40">
          {clip.mediaType === 'video' ? (
            <video src={preview} className="h-full w-full object-cover" muted playsInline preload="metadata" />
          ) : (
            <img src={preview} alt="" className="h-full w-full object-cover" />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm text-zinc-100">{clip.label}</p>
          <p className="text-xs text-zinc-500">
            {clip.mediaType === 'video' ? t('creative.publish.mediaVideo') : t('creative.publish.mediaImage')}
            {clip.isPreferred ? ` · ${t('creative.publish.preferred')}` : ''}
          </p>
        </div>
      </label>
    </li>
  )
}
